import ky from 'ky'
import { action, computed, makeObservable, observable } from 'mobx'

interface HTTPResponseStoreConstructorParams {
    response: Promise<any> | any
    isOriginalResponse?: boolean
    requestClient?: any
    requestData?: {
        url?: string
        options?: any
        settings?: any
    }
    method?: typeof ky.get | typeof ky.post | typeof ky.put | typeof ky.patch | typeof ky.delete
}

export class HTTPResponseStore {
    response: Promise<any> | null = null
    content: any = null
    error: string | null = null

    isLoading: boolean = true
    isOriginalResponse: boolean = false

    requestClient: any = null
    requestData: HTTPResponseStoreConstructorParams['requestData'] = {}
    method: HTTPResponseStoreConstructorParams['method'] | null = null
    private abortController: AbortController | null = null

    constructor({
        response,
        isOriginalResponse = false,
        requestClient,
        requestData,
        method,
    }: HTTPResponseStoreConstructorParams) {
        makeObservable(this, {
            content: observable,
            error: observable,
            isLoading: observable,
            isError: computed,
            setContent: action,
            setError: action,
            setIsLoading: action,
        })

        if (isOriginalResponse === true) {
            this.isOriginalResponse = true
        }

        if (response) {
            this.setResponse(response)
        }

        if (requestClient) {
            this.requestClient = requestClient
        }

        if (requestData) {
            this.requestData = requestData
        }

        if (method) {
            this.method = method
        }
    }

    setContent(content: any): void {
        this.content = content
    }

    setError(error: string | null): void {
        this.error = error
    }

    get isError(): boolean {
        return this.error !== null
    }

    setIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading
    }

    setResponse(response: Promise<any> | any): void {
        if (response instanceof Promise) {
            let processedResponse = response

            if (!this.isOriginalResponse) {
                processedResponse = response.then((res: any) => res.json())
            }

            this.response = processedResponse
                .then((res: any) => {
                    this.setContent(res)

                    // Кешування GET запитів
                    if (this.method === ky.get && this.requestClient && this.requestData) {
                        const requestKey = this.requestClient.getRequestKey({ ...this.requestData })
                        
                        // Добавляем в кеш для повторного использования
                        this.requestClient.cacheResponse.set(requestKey, this)
                        
                        // Удаляем из pendingRequests после завершения
                        this.requestClient.pendingRequests.delete(requestKey)
                        
                        // Автоочистка кеша через 30 секунд (можно настроить)
                        setTimeout(() => {
                            if (this.requestClient.cacheResponse.has(requestKey)) {
                                this.requestClient.cacheResponse.delete(requestKey)
                            }
                        }, 30000)
                    }
                    return res
                })
                .catch(async (error: any) => {
                    // Игнорируем AbortError - это нормальная отмена запроса
                    if (error.name === 'AbortError') {
                        return
                    }

                    if (error.response) {
                        const clonedResponse = error.response.clone()

                        try {
                            const errorData = await clonedResponse.json()
                            this.setError(errorData['hydra:description'] || errorData.message || 'Unknown error')
                        } catch (e) {
                            // Якщо не можемо розпарсити JSON - використовуємо статус
                            this.setError(`HTTP ${error.response.status}: ${error.response.statusText}`)
                        }
                    } else {
                        this.setError(error.message || 'Network error')
                    }

                    // Удаляем из pendingRequests при ошибке
                    if (this.method === ky.get && this.requestClient && this.requestData) {
                        const requestKey = this.requestClient.getRequestKey({ ...this.requestData })
                        this.requestClient.pendingRequests.delete(requestKey)
                    }

                    throw error
                })
                .finally(() => {
                    this.setIsLoading(false)

                    // Очистка runningRequest после завершения
                    if (
                        this.requestClient &&
                        this.requestData?.url &&
                        this.requestClient.runningRequest.has(
                            this.requestClient.getRequestKey({ ...this.requestData })
                        )
                    ) {
                        this.requestClient.runningRequest.delete(
                            this.requestClient.getRequestKey({ ...this.requestData })
                        )
                    }
                })
        }
    }

    getResponse(): Promise<any> | null {
        return this.response
    }

    /**
     * Скасувати запит якщо він ще виконується
     */
    abort(): void {
        if (this.abortController) {
            this.abortController.abort()
            this.abortController = null
        }

        // Також скасовуємо через requestClient якщо є
        if (this.requestClient && this.requestData) {
            const requestKey = this.requestClient.getRequestKey({ ...this.requestData })
            if (this.requestClient.runningRequest.has(requestKey)) {
                const controller = this.requestClient.runningRequest.get(requestKey)
                controller?.abort()
                this.requestClient.runningRequest.delete(requestKey)
            }
        }

        this.setIsLoading(false)
        this.setError('Request aborted')
    }

    /**
     * Встановити AbortController для можливості скасування
     */
    setAbortController(controller: AbortController): void {
        this.abortController = controller
    }
}
